import api from '../service/api.service';
import { ActiveSortField, User, UserRoleApiItem, PaginationQueryParams, ResultSet } from '../../types';
import { parseQueryFilters } from '../../helpers/parseQueryFilters';
import { parseQuerySortFields } from '../../helpers/parseQuerySortFields';

const BASE_URL = '/v1/firm-users';
const FO_BASE_URL = '/v1/fo/firm-users';
const ROLES_BASE_URL = '/v1/fo/user-roles';

export const getUsers = async ({
  size,
  page,
  count,
  match,
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  const queryFilters = parseQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField as ActiveSortField);

  const targetUrl = `${BASE_URL}?size=${size}&page=${page}&count=${count}&match=${match || ''}${queryFilters || ''
    }${sortFilters || ''}`;

  return await api.get<ResultSet<User>>(targetUrl);
};

export const getUserRoles = async () => {
  return await api.get<{ roles: UserRoleApiItem[] }>(ROLES_BASE_URL);
};

export const createUser = async (data: {
  phone: string;
  role: string;
  roleId: number;
  firstName: string;
  lastName: string;
  email: string;
  firmId: number;
}) => {
  const targetUrl = `${BASE_URL}`;

  return await api.post<User>(targetUrl, data);
};

export const getUser = async (id: string | number) => {
  const targetUrl = `${BASE_URL}/${id}`;

  return await api.get<User>(targetUrl);
};

export const updateUser = async (
  lawyerId: number | string,
  data: {
    role?: string;
    roleId?: number;
    firstName?: string;
    lastName?: string;
  },
) => {
  const targetUrl = `${BASE_URL}/${lawyerId}`;

  return await api.put<User>(targetUrl, data);
};

export const resetUserAccount = async (id: string | number) => {
  const targetUrl = `${BASE_URL}/${id}/reset`;

  return await api.post<void>(targetUrl, {});
};

export const deActivateUserAccount = async (id: string | number) => {
  const targetUrl = `${FO_BASE_URL}/${id}/deactivate`;

  return await api.post<void>(targetUrl, {});
};

export const activateUserAccount = async (id: string | number) => {
  const targetUrl = `${FO_BASE_URL}/${id}/activate`;

  return await api.post<void>(targetUrl, {});
};

export const checkUserPin = async (id: number, pin: string) => {
  const targetUrl = `${BASE_URL}/${id}/check-pin`;

  return await api.put<{ isValid: boolean }>(targetUrl, { pin });
};

export const setUserPin = async (id: number, newPin: string, oldPin?: string) => {
  const targetUrl = `${BASE_URL}/${id}/set-pin`;

  return await api.put<void>(targetUrl, { newPin, ...(oldPin && { oldPin }) });
};

export interface UserApiCredentials {
  name: string | null;
  client_secret: string | null;
  client_id: string | null;
  roles?: Array<number | string | { id?: number | string; roleId?: number | string }>;
}

export const getApiCredentials = async (id: number | string) => {
  const targetUrl = `${BASE_URL}/${id}/get_api_credentials`;

  return await api.get<UserApiCredentials>(targetUrl);
};

export const createApiCredentials = async (
  id: number | string,
  roles: Array<string | number>,
  name?: string,
) => {
  const targetUrl = `${BASE_URL}/${id}/create_api_credentials`;

  const roleIds = roles.map((role) => Number(role));

  return await api.post<UserApiCredentials>(targetUrl, { roles: roleIds, ...(name && { name }) });
};

export const updateApiCredential = async (
  id: number | string,
  clientId: string,
  name: string,
  roles: Array<string | number>,
) => {
  const targetUrl = `${BASE_URL}/${id}/edit_api_credentials`;

  const roleIds = roles.map((role) => Number(role));

  return await api.put<UserApiCredentials>(targetUrl, {
    ['clientId']: clientId,
    name,
    roles: roleIds,
  });
};

export const deleteApiCredential = async (id: number | string, clientId: string) => {
  const targetUrl = `${BASE_URL}/${id}/delete_api_credentials`;

  return await api.delete<void>(targetUrl, {
    data: {
      ['client_id']: clientId,
    },
  });
};

export const regenerateApiCredentials = async (id: number | string, clientId?: string) => {
  const targetUrl = `${BASE_URL}/${id}/regenerate_api_credentials`;

  return await api.put<UserApiCredentials>(targetUrl, {
    ...(clientId && { ['client_id']: clientId }),
  });
};
